import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import TeamEditForm from "../../../../../components/dashboardcomponents/People/Teams/TeamEdit/TeamEditForm";
import TeamEditMembersSection from "../../../../../components/dashboardcomponents/People/Teams/TeamEdit/TeamEditMembersSection";
import { buildTeamEditForm } from "../../../../../components/dashboardcomponents/People/Teams/TeamEdit/teamEditHelpers";
import {
  TEAM_EDIT_CANCEL,
  TEAM_EDIT_SAVE,
} from "../../../../../components/dashboardcomponents/People/Teams/teamEvents";
import { pageSubtitleSx, pageTitleSx, TEAM_FORM_WIDTH } from "../../../../../components/dashboardcomponents/People/Teams/teamFormStyles";
import { buildTeamPayloadFromForm } from "../../../../../utils/teamMappers";
import useTeamFormOptions from "../../../../../hooks/useTeamFormOptions";
import usePeoplePermissions from "../../../../../hooks/usePeoplePermissions";
import {
  clearTeamDetail,
  fetchTeamById,
  updateTeam,
} from "../../../../../store/features/teamsApi";
import { TeamEditPageSkeleton } from "../../../../../components/dashboardcomponents/People/Teams/TeamLoadingSkeletons";
import {
  firstValidationError,
  hasValidationErrors,
  validateTeamForm,
} from "../../../../../components/dashboardcomponents/People/peopleFormValidation";

export default function TeamEditPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail, detailLoading } = useSelector((state) => state.peopleTeams);
  const { groupOptions, agentOptions, loading: formOptionsLoading } = useTeamFormOptions();
  const perms = usePeoplePermissions();

  const initialForm = useMemo(
    () => (detail?.id === teamId ? buildTeamEditForm(detail) : null),
    [detail, teamId]
  );
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (teamId) dispatch(fetchTeamById({ teamId }));
    return () => dispatch(clearTeamDetail());
  }, [teamId, dispatch]);

  useEffect(() => {
    if (initialForm) setForm(initialForm);
  }, [initialForm]);

  useEffect(() => {
    if (!detailLoading && detail && !perms.teams.canEdit) {
      toast.error("You don't have permission to edit teams");
      navigate(`/people/teams/${teamId}`);
    }
  }, [detail, detailLoading, perms.teams.canEdit, navigate, teamId]);

  const saveTeam = useCallback(async () => {
    if (!form || saving) return;
    const errors = validateTeamForm(
      {
        name: form.name,
        description: form.description,
        groupIds: form.groupIds,
      },
      { requireGroup: false }
    );
    if (hasValidationErrors(errors)) {
      toast.error(firstValidationError(errors));
      return;
    }

    setSaving(true);
    const body = buildTeamPayloadFromForm(form);
    const result = await dispatch(updateTeam({ teamId, body }));
    setSaving(false);
    if (updateTeam.fulfilled.match(result)) {
      toast.success(result.payload?.message || "Team changes saved");
      navigate(`/people/teams/${teamId}`);
    } else {
      toast.error(result.payload?.message || "Failed to save team");
    }
  }, [form, saving, dispatch, teamId, navigate]);

  useEffect(() => {
    const onSave = () => saveTeam();
    const onCancel = () => navigate(`/people/teams/${teamId}`);
    window.addEventListener(TEAM_EDIT_SAVE, onSave);
    window.addEventListener(TEAM_EDIT_CANCEL, onCancel);
    return () => {
      window.removeEventListener(TEAM_EDIT_SAVE, onSave);
      window.removeEventListener(TEAM_EDIT_CANCEL, onCancel);
    };
  }, [saveTeam, navigate, teamId]);

  if ((detailLoading && !detail) || (detail && !form)) {
    return <TeamEditPageSkeleton />;
  }

  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontFamily: "Inter", color: "#405261" }}>Team not found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: 3,
        py: 3,
      }}
    >
      <Box sx={{ maxWidth: TEAM_FORM_WIDTH, width: "100%", mx: "auto" }}>
        <Typography sx={pageTitleSx}>Edit Teams</Typography>
        <Typography sx={pageSubtitleSx}>
        Update team members routing permissions and workflow settings 
       </Typography>

        <Box sx={{ mt: 3 }}>
          <TeamEditForm
            form={form}
            onChange={setForm}
            groupOptions={groupOptions}
            groupsLoading={formOptionsLoading}
          />
        </Box>

        <TeamEditMembersSection
          members={form.members}
          onMembersChange={(members) => setForm({ ...form, members })}
          agentOptions={agentOptions}
          agentsLoading={formOptionsLoading}
          canManageMembers={perms.teams.canManageMembers}
        />
      </Box>
    </Box>
  );
}
